import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import shortid from 'shortid';
import { Grid } from '@usealloy/component-library';

import TagSquare from '~/components/TagSquare';
import TagNodeInReview from './TagNodeInReview';
import { UserContext } from '~/dashboard/context';
import { tooltipZIndexModifiers } from '~/constants/reactstrap';


const TagsListPropTypes = {
  small: PropTypes.bool,
  padClass: PropTypes.string,
  tags: PropTypes.array.isRequired,
  outcome: PropTypes.string,
  entityToken: PropTypes.string,
  evaluationTags: PropTypes.array,
  evaluationToken: PropTypes.string,
  isPortfolioEvaluation: PropTypes.bool,
  dimensions: PropTypes.array,
  workflowToken: PropTypes.string,
  workflowVersion: PropTypes.number,
  includePendoAnchor: PropTypes.bool,
};

const TagsListDefaultProps = {
  small: false,
  padClass: 'pad-24-32',
  includePendoAnchor: true,
};

/**
 * @typedef {Object} Tag
 * @property {string} tag_name
 * @property {string=} customer_tag_id
 * @property {string=} description
 * @property {string=} tag_color
 */

/**
 * @param {Object} props
 * @param {Tag[]} props.tags
 * @param {string} props.padClass
 */
function TagsList({
  small,
  tags,
  outcome,
  padClass,
  entityToken = '',
  evaluationTags = [],
  evaluationToken = '',
  isPortfolioEvaluation = false,
  dimensions = [],
  workflowToken,
  workflowVersion,
  includePendoAnchor = true,
}) {
  const id = `_${shortid.generate()}`;
  const { featureFlags } = useContext(UserContext);

  if (small) {
    /**
     * Token props are not assumed to be available in this block,
     * since the TagSquare component does not use them.
     */
    return (
      <ul className="inline tag-list">
        {tags.map(tag => (
          <React.Fragment key={tag.customer_tag_id}>
            <li id={`${id}-${tag.customer_tag_id}-li`}>
              <TagSquare
                id={`${id}-${tag.customer_tag_id}`}
                data-testid={`${id}-${tag.customer_tag_id}`}
                customClasses="fas fa-fw fa-lg clickable-element"
                color={tag.tag_color}
                tooltipText={tag.description}
              />
            </li>
            <Tooltip
              target={`${id}-${tag.customer_tag_id}-li`}
              placement="bottom"
              delay={0}
              modifiers={tooltipZIndexModifiers}
            >
              {tag.tag_name}
            </Tooltip>
          </React.Fragment>
        ))}
      </ul>
    );
  }

  if (!tags.length) {
    return (
      <p className="light-grey">
        <i>No tags were returned for this evaluation.</i>
      </p>
    );
  }

  if (featureFlags?.ruleExplainabilityPhase > 0) {
    return (
      <div {...(includePendoAnchor && { 'data-pendo-anchor': 'evaluation-explainability-tags-card' })}>
        <Grid
          colGap={2}
          rowGap={2}
        >
          {tags.map(tag => (
            <Grid.Item columns={{ md: 12, lg: 6, xl: 4 }} key={tag.customer_tag_id || tag.tag_name}>
              <TagNodeInReview
                tag={tag}
                outcome={outcome}
                entityToken={entityToken}
                evaluationTags={evaluationTags}
                evaluationToken={evaluationToken}
                isPortfolioEvaluation={isPortfolioEvaluation}
                dimensions={dimensions}
                workflowToken={workflowToken}
                workflowVersion={workflowVersion}
              />
            </Grid.Item>
          ))}
        </Grid>
      </div>
    );
  }

  return (
    <div className={`evaluation-tags flex start ${padClass}`}>
      {tags.map(tag => (
        <span key={tag.tag_name} className="tag nowrap small-text">
          <TagSquare
            id={`${id}-${tag.customer_tag_id}`}
            data-testid={`${id}-${tag.customer_tag_id}`}
            color={tag.tag_color}
            tooltipText={tag.description}
          />
          <span className="grey">{tag.tag_name}</span>
        </span>
      ))}
    </div>
  );
}
TagsList.propTypes = TagsListPropTypes;
TagsList.defaultProps = TagsListDefaultProps;

export default TagsList;
